import type { RefObject } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CADGridBackground } from "@/components/CADGridBackground";
import { cn } from "@/lib/utils";

export type ScanPhase = "ready" | "scanning" | "processing";

interface ViewfinderProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  cameraActive: boolean;
  phase: ScanPhase;
  preview: string | null;
  docName?: string | null;
  dropActive: boolean;
  onDrop: (file: File) => void;
  onDragState: (active: boolean) => void;
  onPickFile: () => void;
}

const BRACKET = "absolute h-7 w-7 border-safety/80";

export function Viewfinder({ videoRef, cameraActive, phase, preview, docName, dropActive, onDrop, onDragState, onPickFile }: ViewfinderProps) {
  const sweeping = phase !== "ready";

  return (
    <div
      data-testid="scanner-viewfinder"
      onDragOver={(e) => { e.preventDefault(); onDragState(true); }}
      onDragLeave={() => onDragState(false)}
      onDrop={(e) => {
        e.preventDefault();
        onDragState(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onDrop(file);
      }}
      className={cn(
        "relative aspect-[3/4] w-full overflow-hidden rounded-3xl border bg-obsidian-deep transition-[border-color] duration-300 sm:aspect-video",
        dropActive ? "border-safety" : "border-cad/40",
      )}
    >
      <video
        ref={videoRef}
        data-testid="scanner-video"
        playsInline
        muted
        autoPlay
        className={cn("absolute inset-0 h-full w-full object-cover transition-opacity duration-500", cameraActive && !preview ? "opacity-100" : "opacity-0")}
      />

      <AnimatePresence>
        {preview && (
          <motion.img
            key="preview"
            data-testid="scanner-preview"
            src={preview}
            alt="Captured plan"
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </AnimatePresence>

      <CADGridBackground opacity={cameraActive || preview ? 0.45 : 0.7} showCoordinates={false} />

      {docName && !preview && (
        <div data-testid="scanner-doc-preview" className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-safety/50 bg-white/[0.02]">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="text-safety" aria-hidden>
              <path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M5 3h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
            </svg>
          </span>
          <span className="max-w-[80%] truncate font-display text-base font-semibold text-white">{docName}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cad">Plan ready</span>
        </div>
      )}

      {!cameraActive && !preview && !docName && (
        <button
          type="button"
          data-testid="scanner-dropzone"
          onClick={onPickFile}
          className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-cad/50 bg-white/[0.02]">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="text-safety" aria-hidden>
              <path d="M12 16V5" /><path d="m7.5 9.5 4.5-4.5 4.5 4.5" /><path d="M4 15.5v2.5a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2.5" />
            </svg>
          </span>
          <span className="font-display text-lg font-semibold text-white">Drop a plan here</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cad">PDF · JPG · PNG · DWG export</span>
        </button>
      )}

      <span aria-hidden className={cn(BRACKET, "left-4 top-4 rounded-tl-lg border-l border-t")} />
      <span aria-hidden className={cn(BRACKET, "right-4 top-4 rounded-tr-lg border-r border-t")} />
      <span aria-hidden className={cn(BRACKET, "bottom-4 left-4 rounded-bl-lg border-b border-l")} />
      <span aria-hidden className={cn(BRACKET, "bottom-4 right-4 rounded-br-lg border-b border-r")} />

      <AnimatePresence>
        {sweeping && (
          <motion.span
            key="scanline"
            data-testid="scanner-scanline"
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="animate-scanline pointer-events-none absolute inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-safety to-transparent shadow-[0_0_24px_2px_rgba(255,91,0,0.55)]"
          />
        )}
      </AnimatePresence>

      <span
        data-testid="scanner-coordinates"
        className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-md border border-cad/40 bg-obsidian/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/80 backdrop-blur"
      >
        X:1020, Y:740
      </span>
    </div>
  );
}
